import AvenyTheme from '../../../BaseComponents/ThemeProvider/AvenyTheme';
import { AttributeValueSizeType } from '../AttributeValue/AttributeValue';
import { IClientAttributeModel } from '../../../Types/ClientModelTypes';
import Logger from '../../../Utils/Logger';

const WIDTH_OF_W_DEFAULT = 17.53;
export const ATTRIBUTE_VALUE_TYPOGRAPHY_VARIANT = 'h6';

export enum AttributeWidth {
  small = 120,
  medium = 248,
  large = 512,
}

const getAttributeValueTextWidth = (text: string): number => {
  const testSpan = document.createElement('span');
  const attributeValueStyles =
    AvenyTheme.typography[ATTRIBUTE_VALUE_TYPOGRAPHY_VARIANT];
  testSpan.style.cssText = `font-family: ${attributeValueStyles.fontFamily};
                        font-size: ${attributeValueStyles.fontSize};
                        font-weight: ${attributeValueStyles.fontWeight};
                        visibility: hidden;`;

  testSpan.innerText = text;

  document.body.appendChild(testSpan);
  let width = testSpan.getBoundingClientRect().width;

  if (!width) {
    width = WIDTH_OF_W_DEFAULT * text.length;
  }

  document.body.removeChild(testSpan);

  return width;
};

const WIDTH_OF_W = getAttributeValueTextWidth('W');

export const getAttributeValueSize = (
  widthInPixels: number
): AttributeValueSizeType => {
  let size: AttributeValueSizeType = 'medium';

  if (
    widthInPixels > AttributeWidth.medium &&
    widthInPixels <= AttributeWidth.large
  ) {
    size = 'large';
  } else if (widthInPixels > AttributeWidth.large) {
    size = 'ownblock';
  }

  return size;
};

const getEnumAttributeValuesMaxWidth = (
  attr: IClientAttributeModel
): number => {
  const valueDisplayLabels = attr.EnumValues
    ? Array.from(attr.EnumValues, ([key, value]) => value)
    : [];

  if (valueDisplayLabels.length === 0) {
    Logger.error(Error(`Attribute ${attr.AttributeName} has no enum values`));
  }

  const valueMaxWidth = valueDisplayLabels.reduce(
    (maxWidth, valueDisplayLabel) => {
      const valueDisplayLabelMaxWidth = valueDisplayLabel.length * WIDTH_OF_W;

      if (valueDisplayLabelMaxWidth > maxWidth) {
        maxWidth = valueDisplayLabelMaxWidth;
      }

      return maxWidth;
    },
    0
  );
  return valueMaxWidth + attr.AttributeName.length;
};

/**
 * Gets max width of an string attribute, if MaxInputLength is undefined returns undefined
 * @param attr Attribute of datatype String
 */
const getStringAttributeMaxWidth = (
  attr: IClientAttributeModel,
  table?: boolean
): number | undefined => {
  if (attr.MaxInputLength == null) {
    return undefined;
  }
  const titleLength = attr.AttributeName.length;
  if (table) {
    return Math.max(attr.MaxInputLength, titleLength) * WIDTH_OF_W;
  }
  return (attr.MaxInputLength + titleLength) * WIDTH_OF_W;
};

const getStringAttributeValueSize = (
  attr: IClientAttributeModel,
  table?: boolean
): AttributeValueSizeType => {
  const maxWidth = getStringAttributeMaxWidth(attr, table);
  if (maxWidth == null) {
    return table ? 'ownblock' : 'medium';
  }

  return getAttributeValueSize(maxWidth);
};

const getEnumAttributeValueSize = (
  attr: IClientAttributeModel
): AttributeValueSizeType => {
  const maxWidth = getEnumAttributeValuesMaxWidth(attr);
  return getAttributeValueSize(maxWidth);
};

const getMinAttributeValueSize = (
  size: AttributeValueSizeType,
  cap: AttributeValueSizeType
): AttributeValueSizeType => {
  switch (size) {
    case 'small':
      return size;
    case 'medium':
      return cap === 'small' ? cap : size;
    case 'large':
      return cap === 'small' || cap === 'medium' ? cap : size;
    case 'ownblock':
      return cap;
  }
};

export const getAttributeValueAutoSize = (
  attr: IClientAttributeModel,
  cap?: AttributeValueSizeType,
  table?: boolean
): AttributeValueSizeType => {
  let size: AttributeValueSizeType = 'small';
  switch (attr.DataType) {
    case 'Boolean':
    case 'Date':
    case 'Time':
    case 'Currency':
    case 'Int':
    case 'Float':
      return 'medium';
    case 'DateTime':
      size = 'medium';
      break;
    case 'String':
      size = getStringAttributeValueSize(attr, table);
      break;
    case 'ID':
      size = 'large';
      break;
    case 'Enum':
      size = getEnumAttributeValueSize(attr);
      break;
    default:
      Logger.error(Error(`Unknown datatype: ${attr.DataType}`));
      size = 'medium';
      break;
  }
  return cap ? getMinAttributeValueSize(size, cap) : size;
};

export const getAttributeWidth = (
  sizeType: AttributeValueSizeType | undefined
): string | undefined => {
  switch (sizeType) {
    case 'large':
      return `${AttributeWidth.large}px`;
    case 'small':
      return `${AttributeWidth.small}px`;
    case 'ownblock':
      return undefined;
    default:
      return `${AttributeWidth.medium}px`;
  }
};

export const attributeValueSizeToGridSize = (
  size: AttributeValueSizeType,
  isList?: boolean
) => {
  switch (size) {
    case 'small':
      return isList ? 4 : 3;
    default:
      return isList ? 8 : 6;
  }
};

export const getRelativeAttributeWidth = (
  sizeType: AttributeValueSizeType | undefined
): string | undefined => {
  switch (sizeType) {
    case 'large':
    case 'ownblock':
      return '100%';
    default:
      return '50%';
  }
};
